import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const toPounds = (pence) => (pence == null ? "" : (pence / 100).toFixed(2));
const toPence = (pounds) => (pounds === "" ? null : Math.round(parseFloat(pounds) * 100));

const blank = () => ({ price: undefined, sale: undefined, clearSale: false, active: undefined, soldOut: undefined, stock: undefined, clearStock: false, availStart: undefined, availEnd: undefined });

export default function AdminAvailability() {
  const [products, setProducts] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/products").then((r) => {
      setProducts(r.data.filter((p) => !p.archived));
      setDrafts({});
    }).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, []);

  const setField = (id, field, value) => {
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] || blank()), [field]: value } }));
  };
  const draftFor = (id) => drafts[id] || blank();
  const dirtyCount = Object.keys(drafts).length;

  const saveAll = () => {
    const updates = [];
    for (const [id, d] of Object.entries(drafts)) {
      const p = products.find((x) => x.id === id);
      const item = { id, base_updated_at: p?.updated_at };
      if (d.price !== undefined) item.price_pence = toPence(d.price);
      if (d.clearSale) item.clear_sale = true;
      else if (d.sale !== undefined) item.sale_price_pence = toPence(d.sale);
      if (d.active !== undefined) item.active = d.active;
      if (d.soldOut !== undefined) item.sold_out = d.soldOut;
      if (d.clearStock) item.clear_stock = true;
      else if (d.stock !== undefined) item.stock_quantity = d.stock === "" ? null : parseInt(d.stock, 10);
      if (d.availStart !== undefined) item.availability_start = d.availStart || null;
      if (d.availEnd !== undefined) item.availability_end = d.availEnd || null;
      updates.push(item);
    }
    setSaving(true);
    api.post("/admin/products/quick-update", { updates })
      .then((r) => {
        const failed = r.data.results.filter((x) => !x.ok);
        if (failed.length) {
          failed.forEach((f) => toast.error(`${products.find((p) => p.id === f.id)?.name || "Product"}: ${f.error}`));
          toast.error(`${failed.length} change(s) were rejected — fix them and save again.`);
          const okIds = new Set(r.data.results.filter((x) => x.ok).map((x) => x.id));
          setDrafts((d) => Object.fromEntries(Object.entries(d).filter(([id]) => !okIds.has(id))));
          api.get("/admin/products").then((res) => setProducts(res.data.filter((p) => !p.archived)));
        } else {
          toast.success("All changes saved.");
          load();
        }
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setSaving(false));
  };

  const cell = "w-full min-w-[88px] rounded-lg border border-choco-line bg-choco px-2.5 py-2.5 text-sm text-cream-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus";

  return (
    <div data-testid="admin-availability-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-bold text-cream-text">Prices &amp; availability</h1>
          <p className="mt-1 text-sm text-cream-muted">Quick editing for several products at once. Nothing is saved until you press Save.</p>
        </div>
        <button onClick={saveAll} disabled={!dirtyCount || saving}
          className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-sm font-bold text-choco disabled:opacity-40"
          data-testid="availability-save-button">
          <Save className="h-4 w-4" aria-hidden="true" /> {saving ? "Saving…" : `Save ${dirtyCount ? `(${dirtyCount})` : ""}`}
        </button>
      </div>

      {products === null && <p className="mt-6 text-sm text-cream-muted">Loading…</p>}

      <div className="mt-6 space-y-4">
        {products?.map((p) => {
          const d = draftFor(p.id);
          const dirty = !!drafts[p.id];
          return (
            <section key={p.id} className={`rounded-3xl border bg-choco-light p-5 ${dirty ? "border-gold/60" : "border-choco-line"}`} data-testid={`availability-row-${p.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-serif text-base font-semibold text-cream-text">
                  {p.name}
                  {dirty && <span className="ml-2 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold text-gold">unsaved</span>}
                </p>
                <p className="text-xs text-cream-muted">Current: £{toPounds(p.price_pence)}{p.sale_price_pence != null ? ` · sale £${toPounds(p.sale_price_pence)}` : ""}</p>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                <div>
                  <label htmlFor={`price-${p.id}`} className="text-[10px] font-bold uppercase tracking-widest text-cream-muted">Price £</label>
                  <input id={`price-${p.id}`} inputMode="decimal" placeholder={toPounds(p.price_pence)} value={d.price ?? ""} onChange={(e) => setField(p.id, "price", e.target.value)} className={cell} data-testid={`price-input-${p.id}`} />
                </div>
                <div>
                  <label htmlFor={`sale-${p.id}`} className="text-[10px] font-bold uppercase tracking-widest text-cream-muted">Sale £</label>
                  <input id={`sale-${p.id}`} inputMode="decimal" placeholder={toPounds(p.sale_price_pence) || "—"} value={d.sale ?? ""} onChange={(e) => setField(p.id, "sale", e.target.value)} className={cell} data-testid={`sale-input-${p.id}`} />
                  {p.sale_price_pence != null && (
                    <label className="mt-1 flex items-center gap-1.5 text-[11px] text-cream-muted">
                      <input type="checkbox" checked={d.clearSale} onChange={(e) => setField(p.id, "clearSale", e.target.checked)} data-testid={`clear-sale-${p.id}`} /> Remove sale
                    </label>
                  )}
                </div>
                <div>
                  <label htmlFor={`stock-${p.id}`} className="text-[10px] font-bold uppercase tracking-widest text-cream-muted">Stock</label>
                  <input id={`stock-${p.id}`} inputMode="numeric" placeholder={p.stock_quantity == null ? "unlimited" : String(p.stock_quantity)} value={d.stock ?? ""} onChange={(e) => setField(p.id, "stock", e.target.value)} className={cell} data-testid={`stock-input-${p.id}`} />
                </div>
                <div>
                  <label htmlFor={`start-${p.id}`} className="text-[10px] font-bold uppercase tracking-widest text-cream-muted">Available from</label>
                  <input id={`start-${p.id}`} type="date" value={d.availStart ?? p.availability_start ?? ""} onChange={(e) => setField(p.id, "availStart", e.target.value)} className={cell} data-testid={`avail-start-${p.id}`} />
                </div>
                <div>
                  <label htmlFor={`end-${p.id}`} className="text-[10px] font-bold uppercase tracking-widest text-cream-muted">Available until</label>
                  <input id={`end-${p.id}`} type="date" value={d.availEnd ?? p.availability_end ?? ""} onChange={(e) => setField(p.id, "availEnd", e.target.value)} className={cell} data-testid={`avail-end-${p.id}`} />
                </div>
                <div className="flex flex-col justify-end gap-2">
                  <label className="flex min-h-[40px] items-center gap-2 text-xs font-semibold text-cream-text">
                    <input type="checkbox" className="h-5 w-5" checked={d.active ?? p.active} onChange={(e) => setField(p.id, "active", e.target.checked)} data-testid={`active-toggle-${p.id}`} />
                    On sale
                  </label>
                  <label className="flex min-h-[40px] items-center gap-2 text-xs font-semibold text-cream-text">
                    <input type="checkbox" className="h-5 w-5" checked={d.soldOut ?? p.sold_out} onChange={(e) => setField(p.id, "soldOut", e.target.checked)} data-testid={`soldout-toggle-${p.id}`} />
                    Sold out
                  </label>
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
